export type PracticeMode = 'flashcard' | 'quiz';

export type PracticeScope = 'due' | 'weak' | 'recent' | 'custom' | 'starred';

/**
 * "Review" scopes are FSRS-scheduled: they deplete to zero as you clear
 * them and each rating advances the spaced-repetition schedule. Everything
 * else ("Practice"/drill: starred, recent, custom) is repeatable and
 * **no-stakes** - practicing it must NOT submit an FSRS review or change due
 * dates. `isReviewScope` is the single source of truth for that split.
 */
export const REVIEW_SCOPES: ReadonlySet<PracticeScope> = new Set<PracticeScope>(
  ['due', 'weak']
);

export function isReviewScope(scope: PracticeScope): boolean {
  return REVIEW_SCOPES.has(scope);
}

export interface SessionConfig {
  mode: PracticeMode;
  scope: PracticeScope;
  length: number | 'all';
  domain?: string[];
  user?: string[];
  lessons?: string[];
  /** Targeted concept-id allow-list. When set, the runner filters the
   *  practice queue down to this exact subset client-side. Used by
   *  "Practice these again" CTAs in the session detail view. */
  conceptIds?: string[];
  /** Request freshly-generated quiz questions (bypass the 28h question
   *  cache). Set by "Practice again" / repeat CTAs so a repeated quiz
   *  isn't the identical set. No effect on flashcards. */
  fresh?: boolean;
}

export interface QuestionAttempt {
  /** Verbatim question text shown to the user. */
  question: string;
  /** Canonical correct answer text. */
  correctAnswer: string;
  /** Optional explainer surfaced on reveal. */
  explanation?: string;
  // ---- Quiz-only fields ----
  /** MCQ options shown to the user (in display order). */
  options?: string[];
  /** Whichever option text the user picked (or undefined if skipped). */
  pickedAnswer?: string;
  /** Quiz path only - convenience flag (== `pickedAnswer === correctAnswer`). */
  wasCorrect?: boolean;
}

export interface SessionOutcome {
  conceptId: string;
  conceptName: string;
  grade: 1 | 2 | 3 | 4;
  retention: number;
  /** Per-question attempts for the audit view. Flashcard path always
   *  has length 1; quiz path has 1-2. Optional so legacy localStorage
   *  records without it keep working - the detail view falls back to a
   *  "history not captured" notice. */
  attempts?: QuestionAttempt[];
}
